import { getUserFromSession } from '~/utils/user';
import { fetchCustomerData } from '~/hooks/queries/use-user-data-query.js';
import { getChangeCountryMutationHandler } from '~/hooks/mutations/changeCountryMutationHandler';
import { defineStore, useAuthStore, useCartStore, nextTick, tryUseNuxtApp } from '#imports';

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      user: undefined,
      userBillingData: undefined,
      userLicensesSelected: false,
      userSelectedCountry: '',
      showEURegulationConfirmation: false,
      countryHasTax: false,
      showExTax: false, // used for vat exempt status
      showProductTax: undefined,
      hideNewsletterSignup: false,
    }
  },
  getters: {
    getCurrentUser(state) {
      return state.user;
    },
    getSelectedCountry(state) {
      if (! tryUseNuxtApp()) {
        return '';
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return state.userSelectedCountry || $sessionCookies?.userSelectedCountry.value
    },
    getCountryHasTax(state) {
      return state.countryHasTax
    },
    getCountryCookie() {
      if (! tryUseNuxtApp()) {
        return '';
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return $sessionCookies?.userSelectedCountry.value
    },
    getSelectedCurrency() {
      if (! tryUseNuxtApp()) {
        return ''
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return $sessionCookies?.userSelectedCurrency.value
    },
    getHideNewsletterSignup(state) {
      if (state.hideNewsletterSignup) {
        return state.hideNewsletterSignup
      }

      if (! tryUseNuxtApp()) {
        return false
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return state.hideNewsletterSignup = $sessionCookies?.userHideNewsletterSignup.value ?? false
    },
    getShowProductTax(state) {
      if (state.showProductTax) {
        return state.showProductTax
      }

      if (! tryUseNuxtApp()) {
        return false
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return state.showProductTax = $sessionCookies?.userShowTax.value ?? false
    },
    getUserBillingData(state) {
      return state.userBillingData
    },
    getWooSessionToken() {
      if (! tryUseNuxtApp()) {
        return null;
      }

      const { $sessionCookies } = tryUseNuxtApp()

      return $sessionCookies?.wooSession.value
    },
    isAffiliate (state) {
      return state.user?.roles.nodes.findIndex((role) => role.name === 'yith_affiliate') >= 0;
    },
    isVendor (state) {
      return state.user?.roles.nodes.findIndex((role) => role.name === 'vendor') >= 0;
    },
    isAdmin (state) {
      return state.user?.roles.nodes.findIndex((role) => role.name === 'administrator') >= 0;
    },
    isContributor (state) {
      return state.user?.roles.nodes.findIndex((role) => role.name === 'contributor') >= 0;
    },
  },
  actions: {
    setUser(user) {
      this.user = user;
    },
    setUserLicensesSelected(value) {
      this.userLicensesSelected = value;
    },
    setShowEURegulationConfirmation(value) {
      this.showEURegulationConfirmation = value
    },
    setShowExTax(value) {
      // If user is VAT exempt prices show ex tax.
      this.showExTax = value;
    },
    setCountryHasTax(value) {
      this.countryHasTax = value;
    },
    setShowProductTax(value) {
      // used to toggle tax on products - while cart and mini-cart will still include tax if required.
      if (! tryUseNuxtApp()) {
        return;
      }

      const { $sessionCookies } = tryUseNuxtApp()

      $sessionCookies?.update_user_show_tax(value)

      this.showProductTax = value
    },
    setHideNewsletterSignup(value) {
      if (! tryUseNuxtApp()) {
        return;
      }

      const { $sessionCookies } = tryUseNuxtApp()

      $sessionCookies?.update_user_hide_newsletter_signup(value)

      this.hideNewsletterSignup = value
    },
    async initializeVatExemptStatus() {
      const authStore = useAuthStore();

      // Only for authenticated users
      if (!authStore.isAuthenticated) {
        this.showExTax = false;
        return;
      }

      try {
        // Fetch VAT exemption status from GraphQL
        const { data } = await fetchCustomerData();
        const isVatExempt = data?.customer?.isVatExempt ?? false;

        this.setShowExTax(isVatExempt);
      } catch (error) {
        console.error('Error fetching VAT exemption status:', error);
        this.showExTax = false;
      }
    },
    setUserSelectedCountry(value) {
      this.userSelectedCountry = value
    },
    setWooSessionToken(updateToken, override = false) {
      if (! tryUseNuxtApp()) {
        return;
      }

      if (! updateToken && ! override) {
        // prevent setting empty token
        return
      }

      const { $sessionCookies } = tryUseNuxtApp()

      $sessionCookies?.update_woo_session(updateToken);
    },
    async setUserFromSession() {
      if (this.user) {
        return this.user;
      }

      const authStore = useAuthStore()

      authStore.setAuthLoading(true)

      const userData = await getUserFromSession();

      this.setUser(userData);

      authStore.setAuthLoading(false)

      if (userData?.data?.wooSessionToken) {
        this.setWooSessionToken(userData.data.wooSessionToken);
      }

      // Initialize VAT exemption status for logged-in users
      if (this.user) {
        await this.initializeVatExemptStatus();
      }

      return this.user;
    },
    setUserBillingData(billingData) {
      this.userBillingData = billingData
    },
    setUserBillingAddress(billingAddress) {
      this.userBillingData.billingAddress = billingAddress;
    },
    async fetchUserBillingData(refresh = false) {
      if (this.getUserBillingData && ! refresh) {
        return this.getUserBillingData
      }

      const { data: customerData } = await fetchCustomerData()

      let billingAddress = customerData?.customer?.billing;

      if (! billingAddress.address1) {
        // If address has not been set, fetch country from cookie
        billingAddress.country = this.getSelectedCountry
      }

      this.userBillingData = {
        billingAddress: billingAddress,
        availablePaymentMethods: customerData?.customer?.availablePaymentMethods ?? [],
        vatNumber: customerData?.customer?.vatNumber ?? null,
      }

      /**
       * userBillingData = {
       *  billingAddress,
       *  availablePaymentMethods
       *  vatNumber
       * }
       */
      return this.userBillingData
    },
    invalidateSession() {
      if (! tryUseNuxtApp()) {
        return;
      }

      const { $sessionCookies } = tryUseNuxtApp()

      if (! $sessionCookies) {
        return
      }

      $sessionCookies?.clear_all_session()
    },
    // send mutation to server for user country change
    async changeUserSelectedCountry(countryCode) {
      if (! countryCode) {
        return;
      }

      const { mutationHandler: changeCountry } = getChangeCountryMutationHandler();

      this.setUserSelectedCountry(countryCode)

      await changeCountry({ countryCode })
        .then(async (result) => {

          this.setShowEURegulationConfirmation(result?.data?.updateCountry?.isEuVerifyRequired ?? false)

          this.setCountryHasTax(result?.data?.updateCountry?.isEuCountry ?? false)

          const cartStore = useCartStore()

          // Refresh cart first to get updated tax amounts
          await nextTick();
          await cartStore.refreshCart();

          // After cart refresh, update showExTax based on actual cart tax
          // This handles cases like switching to USA (no VAT) or other tax zones
          const cart = cartStore.getCurrentCart;
          const taxString = cart?.totalTax || '0';
          const taxValue = parseFloat(taxString.replace(/[^\d,.-]/g, '').replace(',', '.'));

          // If cart has no tax after country change, don't show ex-tax pricing
          // If cart has tax, check if customer is VAT exempt (for logged-in users only)
          if (taxValue === 0) {
            this.setShowExTax(false);
          } else {
            // For countries with tax, check actual VAT exemption status
            await this.initializeVatExemptStatus();
          }
        });
    },
  },
});
